import { DEFAULT_PAGE_INDEX_INPUT, DEFAULT_PAGE_SIZE } from "constants/index";

import { useMemo } from "react";

import { Grid, IconButton, Tooltip } from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro";
import { Button, Edit02Icon, EyeIcon, PlusIcon } from "components";
import { Breadcrumbs } from "components/Breadcrumbs/Breadcrumbs";
import { Table } from "components/Table/Table";
import { Paths } from "configs";
import {
  RequestEntity,
  useGetManyBaseRequestControllerRequestEntity,
} from "generated";
import { useNotification } from "hooks/useNotification";
import { useSearchParams, useUpdateSearch } from "hooks/useSearchParams";
import { useLocation, useNavigate } from "react-router-dom";
import { NumberParam, useQueryParams, withDefault } from "use-query-params";
import { convertToUnAccent } from "utils/string";

import { requestColumn } from "./columns";
import { RequestFilter } from "./RequestFilter";

export const RequestList = () => {
  const navigate = useNavigate();
  const { search, pathname } = useLocation();
  const { handleSearchClick } = useUpdateSearch(pathname, search);
  const searchParams = useSearchParams(search);
  const [query, setQuery] = useQueryParams({
    pageIndex: withDefault(NumberParam, DEFAULT_PAGE_INDEX_INPUT),
    pageSize: withDefault(NumberParam, DEFAULT_PAGE_SIZE),
  });
  const { pageIndex, pageSize } = query;

  const { data, isLoading, error, refetch } =
    useGetManyBaseRequestControllerRequestEntity({
      s: JSON.stringify({
        $and: [
          {
            ...(searchParams?.createdAt && searchParams?.createdAt?.length > 0
              ? {
                  $and: [
                    searchParams?.createdAt[0]
                      ? {
                          createdAt: {
                            $gt: searchParams?.createdAt[0],
                          },
                        }
                      : undefined,
                    searchParams?.createdAt[1]
                      ? {
                          createdAt: {
                            $lt: searchParams?.createdAt[1],
                          },
                        }
                      : undefined,
                  ],
                }
              : {}),
          },
          {
            ...(searchParams?.dueDate && searchParams?.dueDate?.length > 0
              ? {
                  $and: [
                    searchParams?.dueDate[0]
                      ? {
                          dueDate: {
                            $gt: searchParams?.dueDate[0],
                          },
                        }
                      : undefined,
                    searchParams?.dueDate[1]
                      ? {
                          dueDate: {
                            $lt: searchParams?.dueDate[1],
                          },
                        }
                      : undefined,
                  ],
                }
              : {}),
          },
          {
            ...(searchParams?.reportCodeOrName
              ? {
                  $or: [
                    {
                      name: {
                        $contL: searchParams?.reportCodeOrName,
                      },
                    },
                    {
                      nameUnAccent: {
                        $contL: convertToUnAccent(
                          searchParams?.reportCodeOrName as string
                        ),
                      },
                    },
                    {
                      code: {
                        $contL: searchParams?.reportCodeOrName,
                      },
                    },
                  ],
                }
              : {}),
          },
          {
            ...(searchParams?.status && searchParams?.status !== "All"
              ? {
                  status: {
                    $eq: searchParams?.status,
                  },
                }
              : {}),
          },
          {
            ...(searchParams?.picId
              ? {
                  picId: {
                    $in: searchParams?.picId,
                  },
                }
              : {}),
          },
          {
            ...(searchParams?.clientId
              ? {
                  clientId: {
                    $in: searchParams?.clientId,
                  },
                }
              : {}),
          },
        ],
      }),
      limit: pageSize,
      offset: (pageIndex - 1) * pageSize,
      join: [`pic`, `createdBy`, `updatedBy`, `client`],
    });

  const columns = useMemo<GridColDef<RequestEntity>[]>(() => {
    return [
      ...requestColumn,
      {
        field: "id",
        headerName: "Action",
        renderCell: (params: GridRenderCellParams<RequestEntity>) => {
          return (
            <Grid container>
              <Grid
                item
                xs={6}
                sx={{ display: "flex", justifyContent: "center" }}
              >
                <Tooltip title="View">
                  <IconButton
                    onClick={() => {
                      navigate(`/request/detail/${params.id as string}`);
                    }}
                  >
                    <EyeIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Grid>
              <Grid
                item
                xs={6}
                sx={{ display: "flex", justifyContent: "center" }}
              >
                <Tooltip title="Edit">
                  <IconButton
                    onClick={() => {
                      navigate(`/request/edit/${params.id as string}`);
                    }}
                  >
                    <Edit02Icon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          );
        },
        width: 118,
      },
    ];
  }, [navigate]);
  useNotification(error);
  return (
    <>
      <Breadcrumbs
        rightComponent={
          <Tooltip arrow title="Create New">
            <Button
              onClick={() => {
                navigate(`${Paths.requestCreatePath}`);
              }}
              startIcon={<PlusIcon />}
            >
              Create new
            </Button>
          </Tooltip>
        }
      />
      <Table
        isLoading={isLoading}
        columns={columns}
        rows={data?.data?.data ?? []}
        pageSizeOptions={[10, 50]}
        paginationMode="server"
        rowCount={data?.data?.total ?? 0}
        isPaginationModal={false}
        paginationModel={{
          page: pageIndex - 1,
          pageSize,
        }}
        filterForm={
          <RequestFilter data={searchParams} onSearch={handleSearchClick} />
        }
      />
    </>
  );
};
